import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { registerDevice } from '../../../../queries/ats/playback/devices/api';
import { HttpPlaybackError } from '../../../../queries/ats/playback/error';
import StickyHeader from '../../../../views/app/StickyHeader';
import { DetailsBackground } from '../../../../views/ats-details/details-background/details-background';
import { FallbackProps } from '../../../error-boundary';
import { MotionModal } from '../../../motion-modal/motion-modal';
import { DeviceSelector } from '../device-selector/device-selector';
import css from './player-error-fallback.module.scss';

interface PlayerErrorFallbackProps extends FallbackProps {
  image?: string;
}

export function PlayerErrorFallback({ error, reset, image }: PlayerErrorFallbackProps) {
  return (
    <Fragment>
      <StickyHeader />

      {isDeviceError(error) ? (
        <DeviceErrorFallback error={error} reset={reset} />
      ) : (
        <DefaultErrorModal error={error} reset={reset} />
      )}

      {image !== undefined && <DetailsBackground src={image} />}
    </Fragment>
  );
}

function DefaultErrorModal({ error, reset }: PlayerErrorFallbackProps) {
  const { t } = useTranslation();
  const history = useHistory();

  return (
    <MotionModal classNames={{ content: css.content }} onRequestClose={() => history.goBack()} offsetTop={164} isOpen>
      <h2 className={css.title}>{t('an_error_occured')}</h2>
      <p className={css.message}>{error.message}</p>
      <MotionModal.Button className={css.button} onClick={() => history.goBack()}>
        {t('close')}
      </MotionModal.Button>
    </MotionModal>
  );
}

function DeviceErrorFallback({ reset }: Omit<PlayerErrorFallbackProps, 'image'>) {
  const [shouldSwitchDevice, setShouldSwitchDevice] = useState(false);

  const handleRegisterDevice = useCallback(async () => {
    try {
      await registerDevice();
      reset();
    } catch (err) {
      if (err instanceof HttpPlaybackError && err.code === 'E06') {
        setShouldSwitchDevice(true);
      } else {
        throw err;
      }
    }
  }, [reset]);

  useEffect(() => {
    handleRegisterDevice();
  }, [handleRegisterDevice]);

  if (shouldSwitchDevice) {
    return (
      <DeviceSelector
        onSuccess={() => {
          reset();
        }}
      />
    );
  }

  return null;
}

function isDeviceError(error: Error): error is HttpPlaybackError {
  // E08 indicates device is not found or registered
  // E06 indicates "Creating new device was restricted by device policy."
  if (error instanceof HttpPlaybackError) {
    return Boolean(error.code === 'E08' || error.code === 'E06');
  }

  return false;
}
